import { readJsonSetting } from "@/lib/cron/mark-run";

/**
 * Einstellungen für das Kurzvideo auf der Startseite.
 *
 * Gepflegt unter Admin → System („Kurzvideo Startseite“), gespeichert in
 * `site_settings`. Zwei Wege sind möglich:
 *   • eine Videodatei (MP4/WebM), z. B. aus dem Supabase-Speicher,
 *   • ein YouTube-Link – dann wird der Player erst nach Klick geladen
 *     (youtube-nocookie), vorher fließen keine Daten an Google.
 *
 * Solange keine Adresse gesetzt ist, zeigt die Startseite den Block nicht.
 */
export const INTRO_VIDEO_URL_KEY = "intro_video_url";
export const INTRO_VIDEO_POSTER_KEY = "intro_video_poster";
/** Upload-Datum und Dauer – wird beim Hochladen im Admin gesetzt (VideoObject-Schema). */
export const INTRO_VIDEO_META_KEY = "intro_video_meta";
/** Untertitel-Datei (WebVTT, Adresse) und Transkript (Text) – Barrierefreiheit (WCAG 1.2.2/1.2.3). */
export const INTRO_VIDEO_CAPTIONS_KEY = "intro_video_captions";
export const INTRO_VIDEO_TRANSCRIPT_KEY = "intro_video_transcript";

export type IntroVideoMeta = { uploadDate: string; durationSeconds: number | null };

export type IntroVideoAccess = {
  /** Adresse der WebVTT-Datei (Untertitel, Deutsch) oder null. */
  captions: string | null;
  /** Transkript als Text (Absätze durch Leerzeilen) oder null. */
  transcript: string | null;
};

export type IntroVideoConfig =
  | ({ kind: "file"; src: string; poster: string | null; meta: IntroVideoMeta | null } & IntroVideoAccess)
  | ({ kind: "youtube"; videoId: string; poster: string | null; meta: IntroVideoMeta | null } & IntroVideoAccess);

/** Texte des Video-Blocks – eine Quelle für Seite und VideoObject-Schema. */
export const INTRO_VIDEO_TEXTS = {
  kicker: "Unsere Story in 60 Sekunden",
  title: "Was DMD Studio für Sie macht.",
  description:
    "Eine Minute Motion Design – und Sie wissen, wie wir Design, Marketing und Development für Ihr Business zusammenbringen.",
} as const;

/** YouTube-ID aus den gängigen Link-Formen ziehen; sonst null. */
export function youtubeIdFrom(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\.|^m\./, "");
    if (host === "youtu.be") return u.pathname.slice(1).split("/")[0] || null;
    if (host === "youtube.com" || host === "youtube-nocookie.com") {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      const m = u.pathname.match(/^\/(?:embed|shorts|live)\/([^/?]+)/);
      if (m) return m[1];
    }
  } catch {
    return null;
  }
  return null;
}

export function parseIntroVideo(
  url: string | null,
  poster: string | null,
  meta: IntroVideoMeta | null = null,
  access: Partial<IntroVideoAccess> = {},
): IntroVideoConfig | null {
  const src = (url ?? "").trim();
  if (!src) return null;
  const p = (poster ?? "").trim() || null;
  const a: IntroVideoAccess = {
    captions: (access.captions ?? "").trim() || null,
    transcript: (access.transcript ?? "").trim() || null,
  };
  const id = youtubeIdFrom(src);
  if (id) return { kind: "youtube", videoId: id, poster: p, meta, ...a };
  if (/^https?:\/\//i.test(src) || src.startsWith("/")) return { kind: "file", src, poster: p, meta, ...a };
  return null;
}

function parseMeta(raw: unknown): IntroVideoMeta | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const uploadDate = typeof r.uploadDate === "string" && /^\d{4}-\d{2}-\d{2}/.test(r.uploadDate) ? r.uploadDate : null;
  if (!uploadDate) return null;
  const d = typeof r.durationSeconds === "number" && r.durationSeconds > 0 ? Math.round(r.durationSeconds) : null;
  return { uploadDate, durationSeconds: d };
}

export async function getIntroVideoConfig(): Promise<IntroVideoConfig | null> {
  try {
    const [url, poster, meta, captions, transcript] = await Promise.all([
      readJsonSetting<string>(INTRO_VIDEO_URL_KEY),
      readJsonSetting<string>(INTRO_VIDEO_POSTER_KEY),
      readJsonSetting<unknown>(INTRO_VIDEO_META_KEY),
      readJsonSetting<string>(INTRO_VIDEO_CAPTIONS_KEY),
      readJsonSetting<string>(INTRO_VIDEO_TRANSCRIPT_KEY),
    ]);
    return parseIntroVideo(url, poster, parseMeta(meta), { captions, transcript });
  } catch {
    return null;
  }
}
